import { useLegendState as useState } from "@/hooks/useLegendState";
import { ChevronDown, Settings2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  X,
  Plus,
  Bot,
  Search,
  TrendingUp,
  Pencil,
  Code,
  Globe,
  FileText,
  CheckCircle,
  Languages,
  Database,
  FileSearch,
  Check,
} from "lucide-react";
import type { Agent, AgentNodeType, AgentOutputFormat } from "@/types/aiToolkit";
import { CAPABILITY_MODULES } from "@/data/aiToolkit";
import {
  LENS_OPTIONS,
  OUTPUT_FORMAT_OPTIONS,
} from "@/lib/agentBuildConstants";
import { defaultOutputRequirements } from "@/lib/validateAgentBuild";
import { AgentWorkflowFields } from "@/components/agents/AgentWorkflowFields";

const PREFERRED_MODEL_OPTIONS = [
  { value: "__default__", label: "Use worker profile default" },
  { value: "openai/gpt-4o-mini", label: "GPT-4o Mini (balanced)" },
  { value: "openai/gpt-4o", label: "GPT-4o (capable)" },
  { value: "anthropic/claude-3.5-sonnet", label: "Claude 3.5 Sonnet (deep)" },
  { value: "google/gemini-2.5-flash", label: "Gemini 2.5 Flash (fast)" },
];

const getCapabilityIcon = (iconName: string) => {
  const icons: Record<string, typeof Search> = {
    Search,
    TrendingUp,
    Pencil,
    Code,
    Globe,
    FileText,
    CheckCircle,
    Languages,
    Database,
    FileSearch,
  };
  return icons[iconName] || Bot;
};

export interface AgentAdvancedSectionProps {
  value: Partial<Agent>;
  onChange: (patch: Partial<Agent>) => void;
  showFolderPicker?: boolean;
  folderOptions: { id: string; label: string }[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function AgentAdvancedSection({
  value,
  onChange,
  showFolderPicker = true,
  folderOptions,
  open,
  onOpenChange,
}: AgentAdvancedSectionProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [newSection, setNewSection] = useState("");
  const [outputSchemaDraft, setOutputSchemaDraft] = useState<string | null>(null);

  const isOpen = open ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;

  const setFormData = (updater: Partial<Agent> | ((prev: Partial<Agent>) => Partial<Agent>)) => {
    if (typeof updater === "function") {
      onChange(updater(value));
    } else {
      onChange({ ...value, ...updater });
    }
  };

  const toggleCapabilityModule = (moduleId: string) => {
    setFormData((prev) => {
      const currentModules = prev.capabilityModules || [];
      if (currentModules.includes(moduleId)) {
        return {
          ...prev,
          capabilityModules: currentModules.filter((id) => id !== moduleId),
        };
      }
      return {
        ...prev,
        capabilityModules: [...currentModules, moduleId],
      };
    });
  };

  const isModuleSelected = (moduleId: string) =>
    (value.capabilityModules || []).includes(moduleId);

  const addRequiredSection = () => {
    if (!newSection.trim()) return;
    setFormData((prev) => ({
      ...prev,
      outputRequirements: {
        ...defaultOutputRequirements(),
        ...prev.outputRequirements,
        requiredSections: [
          ...(prev.outputRequirements?.requiredSections || []),
          newSection.trim(),
        ],
      },
    }));
    setNewSection("");
  };

  const removeRequiredSection = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      outputRequirements: {
        ...prev.outputRequirements,
        requiredSections:
          prev.outputRequirements?.requiredSections?.filter((_, i) => i !== index) || [],
      },
    }));
  };

  const outputSchemaText =
    outputSchemaDraft ??
    (value.outputSchema && Object.keys(value.outputSchema).length > 0
      ? JSON.stringify(value.outputSchema, null, 2)
      : "");

  const handleOutputSchemaChange = (text: string) => {
    setOutputSchemaDraft(text);
    if (!text.trim()) {
      onChange({ outputSchema: undefined });
      return;
    }
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      onChange({ outputSchema: parsed });
    } catch {
      // Wait for valid JSON before persisting
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setOpen} id="agent-section-advanced" className="scroll-mt-4">
      <CollapsibleTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-between"
          data-testid="toggle-agent-advanced"
        >
          <span className="flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            Advanced — capabilities, automation & output
          </span>
          <ChevronDown
            className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-4 space-y-6">
        {/* Metadata */}
        <div className="space-y-4 rounded-lg border p-4">
          <h4 className="text-sm font-medium">Metadata</h4>
          {showFolderPicker && (
            <div className="space-y-2">
              <Label>Folder</Label>
              <Select
                value={value.folderId || "__none__"}
                onValueChange={(v) =>
                  onChange({ folderId: v === "__none__" ? null : v })
                }
              >
                <SelectTrigger data-testid="select-agent-folder">
                  <SelectValue placeholder="Select folder" />
                </SelectTrigger>
                <SelectContent>
                  {folderOptions.map((opt) => (
                    <SelectItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={value.category || "general"}
                onValueChange={(v) => onChange({ category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="Research">Research</SelectItem>
                  <SelectItem value="Writing">Writing</SelectItem>
                  <SelectItem value="Analysis">Analysis</SelectItem>
                  <SelectItem value="Creative">Creative</SelectItem>
                  <SelectItem value="Technical">Technical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Agent Type</Label>
              <Select
                value={value.nodeType || "specialist"}
                onValueChange={(v) => onChange({ nodeType: v as AgentNodeType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="specialist">Specialist</SelectItem>
                  <SelectItem value="orchestrator">Orchestrator</SelectItem>
                  <SelectItem value="aggregator">Aggregator</SelectItem>
                  <SelectItem value="validator">Validator</SelectItem>
                  <SelectItem value="iterator">Iterator</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Lens / Perspective</Label>
            <Select
              value={value.lens || "none"}
              onValueChange={(v) => onChange({ lens: v === "none" ? null : v })}
            >
              <SelectTrigger data-testid="select-lens">
                <SelectValue placeholder="Select a lens..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {LENS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label} — {option.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="expert-focus">Expert Focus</Label>
            <Input
              id="expert-focus"
              value={value.expertFocus || ""}
              onChange={(e) => onChange({ expertFocus: e.target.value || null })}
              placeholder="e.g., Rigorous evidence-based research with cited sources"
              data-testid="input-expert-focus"
            />
          </div>
          <div className="space-y-2">
            <Label>Communication Style</Label>
            <Select
              value={value.persona?.communicationStyle || "professional"}
              onValueChange={(v) =>
                setFormData((prev) => ({
                  ...prev,
                  persona: { ...prev.persona!, communicationStyle: v },
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="friendly">Friendly</SelectItem>
                <SelectItem value="academic">Academic</SelectItem>
                <SelectItem value="casual">Casual</SelectItem>
                <SelectItem value="formal">Formal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Preferred Model</Label>
            <Select
              value={value.preferredModel || "__default__"}
              onValueChange={(v) =>
                onChange({ preferredModel: v === "__default__" ? undefined : v })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Default" />
              </SelectTrigger>
              <SelectContent>
                {PREFERRED_MODEL_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Output */}
        <div className="space-y-4 rounded-lg border p-4">
          <h4 className="text-sm font-medium">Output</h4>
          <div className="space-y-2">
            <Label>Output Format</Label>
            <Select
              value={value.outputRequirements?.format || "markdown"}
              onValueChange={(v) =>
                setFormData((prev) => ({
                  ...prev,
                  outputRequirements: {
                    ...defaultOutputRequirements(),
                    ...prev.outputRequirements,
                    format: v as AgentOutputFormat,
                  },
                }))
              }
            >
              <SelectTrigger data-testid="select-output-format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OUTPUT_FORMAT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Output Instructions</Label>
            <Textarea
              value={value.outputRequirements?.instructions || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  outputRequirements: {
                    ...defaultOutputRequirements(),
                    ...prev.outputRequirements,
                    instructions: e.target.value,
                  },
                }))
              }
              placeholder='e.g. "Respond with: Summary, Key findings, Recommended next steps"'
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <Label>Max Length Hint</Label>
            <Input
              value={value.outputRequirements?.maxLengthHint || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  outputRequirements: {
                    ...defaultOutputRequirements(),
                    ...prev.outputRequirements,
                    maxLengthHint: e.target.value,
                  },
                }))
              }
              placeholder="e.g., Under 500 words"
            />
          </div>
          <div className="space-y-2">
            <Label>Required Sections</Label>
            <div className="flex gap-2">
              <Input
                value={newSection}
                onChange={(e) => setNewSection(e.target.value)}
                placeholder="Section name..."
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), addRequiredSection())
                }
              />
              <Button type="button" size="sm" onClick={addRequiredSection}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {value.outputRequirements?.requiredSections?.map((sec, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className="cursor-pointer"
                  onClick={() => removeRequiredSection(index)}
                >
                  {sec}
                  <X className="h-3 w-3 ml-1" />
                </Badge>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Output Schema (JSON)</Label>
            <Textarea
              value={outputSchemaText}
              onChange={(e) => handleOutputSchemaChange(e.target.value)}
              placeholder='{"type":"object","properties":{...}}'
              rows={4}
              className="font-mono text-xs"
            />
          </div>
        </div>

        {/* Legacy capability modules */}
        <div className="space-y-4 rounded-lg border p-4">
          <div>
            <h4 className="text-sm font-medium">Capability Modules</h4>
            <p className="text-xs text-muted-foreground mt-1">
              Legacy skill modules (distinct from workflow skills below)
            </p>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {CAPABILITY_MODULES.map((module) => {
              const IconComponent = getCapabilityIcon(module.icon);
              const selected = isModuleSelected(module.id);
              return (
                <div
                  key={module.id}
                  className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                    selected
                      ? "border-primary bg-primary/5"
                      : "border-border hover-elevate"
                  }`}
                  onClick={() => toggleCapabilityModule(module.id)}
                >
                  <div className={`p-2 rounded-md ${module.color} text-white`}>
                    <IconComponent className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{module.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {module.description}
                    </div>
                  </div>
                  <div
                    className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                      selected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-muted-foreground"
                    }`}
                  >
                    {selected && <Check className="h-3 w-3" />}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="pt-2 border-t">
            <Label>Memory Policy</Label>
            <Select
              value={value.memoryPolicy?.type || "session"}
              onValueChange={(v: "none" | "session" | "persistent") =>
                setFormData((prev) => ({
                  ...prev,
                  memoryPolicy: { ...prev.memoryPolicy!, type: v },
                }))
              }
            >
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Memory</SelectItem>
                <SelectItem value="session">Session Only</SelectItem>
                <SelectItem value="persistent">Persistent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Automation policies */}
        <div className="space-y-4 rounded-lg border p-4">
          <h4 className="text-sm font-medium">Automation policies</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Min confidence (0–1)</Label>
              <Input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={value.confidencePolicy?.minScore ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  onChange({
                    confidencePolicy: {
                      ...value.confidencePolicy,
                      minScore: v === "" ? undefined : Number(v),
                      onLowConfidence: value.confidencePolicy?.onLowConfidence ?? "continue",
                    },
                  });
                }}
                placeholder="e.g. 0.8"
              />
            </div>
            <div className="space-y-2">
              <Label>On low confidence</Label>
              <Select
                value={value.confidencePolicy?.onLowConfidence ?? "continue"}
                onValueChange={(v) =>
                  onChange({
                    confidencePolicy: {
                      ...value.confidencePolicy,
                      minScore: value.confidencePolicy?.minScore,
                      onLowConfidence: v as NonNullable<Agent["confidencePolicy"]>["onLowConfidence"],
                    },
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="continue">Continue</SelectItem>
                  <SelectItem value="pause">Pause</SelectItem>
                  <SelectItem value="branch">Branch</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Min quality (0–1)</Label>
              <Input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={value.qualityPolicy?.minScore ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  onChange({
                    qualityPolicy: {
                      ...value.qualityPolicy,
                      minScore: v === "" ? undefined : Number(v),
                      onLowQuality: value.qualityPolicy?.onLowQuality ?? "continue",
                    },
                  });
                }}
                placeholder="e.g. 0.7"
              />
            </div>
            <div className="space-y-2">
              <Label>On low quality</Label>
              <Select
                value={value.qualityPolicy?.onLowQuality ?? "continue"}
                onValueChange={(v) =>
                  onChange({
                    qualityPolicy: {
                      ...value.qualityPolicy,
                      minScore: value.qualityPolicy?.minScore,
                      onLowQuality: v as NonNullable<Agent["qualityPolicy"]>["onLowQuality"],
                    },
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="continue">Continue</SelectItem>
                  <SelectItem value="pause">Pause</SelectItem>
                  <SelectItem value="branch">Branch</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Workflow automation fields */}
        <div className="rounded-lg border p-4">
          <h4 className="text-sm font-medium mb-4">Workflow & Automation</h4>
          <AgentWorkflowFields
            value={value}
            onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
